// client/src/components/WelcomeModal.jsx
//
// One-time welcome modal shown after a distributor finishes onboarding.
// Highlights 5 features that demonstrate immediate value, with copy tuned
// to the actual workflow (verified inventory, AI bundles, presentations,
// POs, branded storefront).
//
// Behavior:
//   - Shows when dist.welcomed_at IS NULL
//   - On dismiss, POSTs /api/me/welcome which sets welcomed_at
//   - After that, the modal never shows for this distributor again
//   - Skippable via the X button or "Skip" link in addition to the primary CTA
//
// Visual style mirrors the Direction C storefront aesthetic — accent color
// used sparingly, generous whitespace, no decorative gradients.

import { useState } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';

const G = BRAND.colors.primary;

export default function WelcomeModal({ dist, getToken, onDismiss }) {
  const [dismissing, setDismissing] = useState(false);

  const dismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      const t = await getToken();
      const updated = await api.withToken(t).markWelcomed();
      onDismiss(updated);
    } catch {
      // Even if the server call fails, we still want to dismiss locally so
      // the user isn't trapped behind a broken modal. The modal will re-show
      // next session if welcomed_at didn't persist; that's a tolerable retry
      // path. (Most failures here are transient network issues anyway.)
      onDismiss(null);
    } finally {
      setDismissing(false);
    }
  };

  const accent = dist?.brand_color || G;
  const company = dist?.company || 'your company';
  const subdomain = dist?.subdomain;

  // Try to surface the storefront URL the server auto-generated. If for some
  // reason subdomain is null (edge case where auto-generation didn't fire),
  // we fall back to a generic "configure in settings" line.
  const storefrontUrl = subdomain
    ? `${window.location.origin}/store/${subdomain}`
    : null;

  // The 5 feature cards. Order matters — opens with the strongest value
  // claim ("verified inventory") since that's what differentiates the
  // product from competitors, ends with the storefront which often
  // produces an "oh, I get it" reaction.
  const features = [
    {
      icon: '✓',
      title: 'Verified inventory and pricing',
      copy: "Every product shown is confirmed in stock with current wholesale pricing. No more calling suppliers to find out something is discontinued or out of stock — we handle that for you.",
    },
    {
      icon: '✦',
      title: 'AI Bundle Builder',
      copy: 'Describe a scenario — like "executive gifts for 50 healthcare clients under $40" — and get three curated bundles back instantly, matched to your catalog.',
    },
    {
      icon: '▤',
      title: 'Client presentations',
      copy: 'Pick products, set your margin, and send a branded PDF. Quote in minutes instead of hours.',
    },
    {
      icon: '▦',
      title: 'Purchase order builder',
      copy: 'Convert any approved quote into a supplier-ready PO in two clicks. We pre-fill the product details, pricing, and supplier info for you.',
    },
    {
      icon: '◐',
      title: 'Your storefront',
      copy: storefrontUrl
        ? `Your branded catalog is already live at ${storefrontUrl.replace(/^https?:\/\//, '')}. Share the link with clients and they can browse and request quotes directly.`
        : 'A branded catalog at your own URL. Share the link with clients and they can browse and request quotes directly. Configure it in Storefront settings.',
    },
  ];

  return (
    <div
      onClick={dismiss}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(15, 23, 42, 0.6)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '5vh 24px 5vh',
        overflowY: 'auto',
      }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 16, maxWidth: 600, width: '100%',
          position: 'relative', overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}>
        {/* Tinted hero band matches the storefront Direction C feel */}
        <div style={{ background: `${accent}14`, padding: '40px 40px 32px' }}>
          <button
            onClick={dismiss}
            aria-label="Close"
            style={{
              position: 'absolute', top: 16, right: 16,
              background: 'transparent', border: 'none', cursor: 'pointer',
              fontSize: 22, color: '#64748B', lineHeight: 1,
              width: 32, height: 32, borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'inherit',
            }}>
            ×
          </button>
          <div style={{
            fontSize: 12, fontWeight: 600, color: accent,
            letterSpacing: '1.4px', textTransform: 'uppercase', marginBottom: 14,
          }}>
            Welcome aboard
          </div>
          <div style={{
            fontSize: 26, fontWeight: 700, color: '#0F172A',
            letterSpacing: '-0.5px', lineHeight: 1.2, marginBottom: 10,
          }}>
            You're all set up, {company}.
          </div>
          <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.55 }}>
            Here's a quick tour of what you can do. Each of these is ready to use right now.
          </div>
        </div>

        <div style={{ padding: '24px 40px 8px' }}>
          {features.map((f, i) => (
            <div key={f.title}
              style={{
                display: 'flex', gap: 16, padding: '16px 0',
                borderBottom: i < features.length - 1 ? '1px solid #F1F5F9' : 'none',
              }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: `${accent}14`, color: accent,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, fontWeight: 600, flexShrink: 0,
              }}>
                {f.icon}
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#0F172A', marginBottom: 4 }}>
                  {f.title}
                </div>
                <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.55 }}>
                  {f.copy}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{
          padding: '20px 40px 36px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ fontSize: 12, color: '#94A3B8' }}>
            You can revisit your settings anytime from the sidebar.
          </div>
          <button onClick={dismiss} disabled={dismissing}
            style={{
              background: accent, color: '#fff',
              border: 'none', borderRadius: 10, padding: '11px 22px',
              fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
              opacity: dismissing ? 0.7 : 1,
            }}>
            {dismissing ? 'Closing…' : 'Start exploring →'}
          </button>
        </div>
      </div>
    </div>
  );
}
